import { useState, VFC } from "react";

import { Button, Collapse, FormControl, FormLabel, Typography } from "@portex-pro/ui-components";
import { RoutingType } from "api/graphql/generated";
import { AddressType } from "api/types/generated-types";
import LocationPicker from "components/addresses/LocationPicker";
import { Guide } from "components/Guide";
import SingleCalendarPicker from "components/SingleCalendarPicker";
import { DateTime } from "luxon";
import FormSelectView from "pages/shipper/pages/request-quote/components/FormSelectView";
import { Controller, FormProvider, UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IncotermsEnum } from "types/Incoterms";
import { RoutingTypeEnum } from "types/RoutingType";

import RoutingTabPanel from "../../_components/RoutingTabPanel";
import { StepBlock } from "../../_components/StepBlock";
import { useQuoteRequestSelector } from "../../_store";
import { quoteRequestActions } from "../../_store/quoteRequestSliceV2";
import { QuoteStepIndex } from "../../types";
import { FinalSchema, StopSchema } from "../_schemas";

interface RoutingStepTagProps {
  originPort?: { address?: { airport_iata_code?: string; city?: string | null; [key: string]: unknown } | null } | null;
  destinationPort?: {
    address?: { airport_iata_code?: string; city?: string | null; [key: string]: unknown } | null;
  } | null;
  cargoReadyDate?: string | null;
}

const RoutingStepTag: React.FC<RoutingStepTagProps> = ({ originPort, destinationPort, cargoReadyDate }) => {
  const parts: string[] = [];

  if (originPort?.address) {
    const originCode = originPort.address.airport_iata_code;
    const originName = originPort.address.city;
    const originParts = [originName, originCode].filter(Boolean).join(" - ");
    parts.push(originParts);
  }

  if (destinationPort?.address) {
    const destinationCode = destinationPort.address.airport_iata_code;
    const destinationName = destinationPort.address.city;
    const destinationParts = [destinationCode, destinationName].filter(Boolean).join(" - ");
    parts.push(destinationParts);
  }

  let displayDate = "";
  if (cargoReadyDate) {
    const date = DateTime.fromISO(cargoReadyDate);
    if (date.isValid) {
      displayDate = date.toFormat("MMM dd");
    }
  }

  if (parts.filter(Boolean).length === 0) {
    return <></>;
  }

  const displayText = parts.filter(Boolean).join(" – ");

  return (
    <div className="inline-flex items-center bg-blue-50 rounded-md px-2 py-1">
      <span className="text-blue-600 font-bold text-sm">{displayText}</span>
      <span className="text-blue-600 text-sm">&nbsp;{displayDate}</span>
    </div>
  );
};

function mapToAddressType(type: string | null | undefined): AddressType | undefined {
  if (!type) return undefined;
  if (Object.values(AddressType).includes(type as AddressType)) {
    return type as AddressType;
  }
  return undefined;
}

const RoutingStep: VFC<{ methods: UseFormReturn<FinalSchema>; onSubmit: (data: FinalSchema) => void }> = ({
  onSubmit,
  methods,
}) => {
  const { t } = useTranslation(["quoteRequest", "common"]);
  const setQuoteRequestAction = quoteRequestActions.useSetQuoteRequest();
  const setStepIndex = quoteRequestActions.useSetStepIndex();
  const { stepIndex } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const routingType = useWatch({ name: "routing_type", control: methods.control });
  const cargoReadyDate = useWatch({ name: "cargo_ready_date", control: methods.control });
  useWatch({ name: "cargo_ready_date", control: methods.control });

  const existingOriginStop = useWatch({ control: methods.control, name: "originStop" });
  const existingOriginPort = useWatch({ control: methods.control, name: "originPort" });
  const existingDestinationPort = useWatch({ control: methods.control, name: "destinationPort" });
  const existingDestinationStop = useWatch({ control: methods.control, name: "destinationStop" });

  const changeStops = ({
    originStopAddress,
    originPortAddress,
    destinationPortAddress,
    destinationStopAddress,
  }: {
    originStopAddress?: StopSchema["address"];
    originPortAddress?: StopSchema["address"];
    destinationPortAddress?: StopSchema["address"];
    destinationStopAddress?: StopSchema["address"];
  }) => {
    const stops = [
      originPortAddress ? { address: originPortAddress } : existingOriginPort,
      destinationPortAddress ? { address: destinationPortAddress } : existingDestinationPort,
    ];
    if (routingType === RoutingTypeEnum.D2D || routingType === RoutingTypeEnum.D2P) {
      stops.unshift(originStopAddress ? { address: originStopAddress } : existingOriginStop);
    }
    if (routingType === RoutingTypeEnum.D2D || routingType === RoutingTypeEnum.P2D) {
      stops.push(destinationStopAddress ? { address: destinationStopAddress } : existingDestinationStop);
    }
    setQuoteRequestAction({
      stops: stops.map((stop) =>
        !!stop
          ? {
              ...stop,
              address: stop.address === null ? null : { ...stop.address, type: mapToAddressType(stop.address?.type) },
            }
          : { address: null }
      ),
    });
  };

  const submit = (data: FinalSchema) => {
    onSubmit(data);
    setStepIndex(QuoteStepIndex.Service);
    setIsCollapsed(true);
  };

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col gap-6 w-full">
        <StepBlock
          title={t("quoteRequest:routing")}
          headerBoxProps={{ onClick: () => setIsCollapsed(!isCollapsed) }}
          stepNumber={1}
          tag={
            <RoutingStepTag
              originPort={existingOriginPort}
              destinationPort={existingDestinationPort}
              cargoReadyDate={cargoReadyDate}
            />
          }
          footer={
            stepIndex <= QuoteStepIndex.Routing ? (
              <div className="flex justify-between mt-2 w-full">
                <div />
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={methods.handleSubmit(submit)}
                  style={{ width: 150 }}
                >
                  {t("common:next")}
                </Button>
              </div>
            ) : null
          }
        >
          <Collapse in={!isCollapsed}>
            <div className="grid grid-cols-4 gap-x-4 gap-y-6 my-6">
              <Controller
                control={methods.control}
                name="incoterms"
                render={({ field, fieldState }) => (
                  <Guide showGuide={!field.value && !fieldState.error} className="w-full flex flex-col">
                    <FormSelectView
                      value={field.value}
                      items={Object.values(IncotermsEnum)}
                      getItemCopy={(incoterm) => t(`common:incoterms_map.${incoterm}`)}
                      onChange={(value) => setQuoteRequestAction({ load_spec: { incoterms: value } })}
                      formControlProps={{ required: true }}
                      highlight={!!methods.formState.errors.incoterms}
                      label={t("common:incoterms")}
                      fullWidth
                    />
                    {methods.formState.errors.incoterms && (
                      <Typography color="error" variant="caption">
                        {methods.formState.errors.incoterms.message}
                      </Typography>
                    )}
                  </Guide>
                )}
              />
              <div className="col-span-2">
                <Controller
                  control={methods.control}
                  name="routing_type"
                  render={({ field, fieldState }) => (
                    <FormControl fullWidth>
                      <FormLabel required>{t("quoteRequest:selectRouting")}</FormLabel>
                      <Guide showGuide={!field.value && !fieldState.error}>
                        <RoutingTabPanel
                          selected={field.value as unknown as RoutingType}
                          className="guideGroup"
                          onSelect={(routing) => {
                            setQuoteRequestAction({
                              load_spec: { routing_type: routing as unknown as RoutingTypeEnum },
                            });
                          }}
                          hasError={!!methods.formState.errors.routing_type}
                        />
                      </Guide>
                      {methods.formState.errors.routing_type && (
                        <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                          {methods.formState.errors.routing_type.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </div>
              <div />

              {routingType !== undefined && (
                <>
                  {(routingType === "D2D" || routingType === "D2P") && (
                    <div>
                      <Controller
                        control={methods.control}
                        name="originStop.address"
                        render={({ field, fieldState }) => (
                          <Guide showGuide={!field.value && !fieldState.error}>
                            <LocationPicker
                              label={t("quoteRequest:origin")}
                              mode={"AIR"}
                              startIconPallete="grey"
                              toDoor
                              required
                              highlight={!!methods.formState.errors.originStop}
                              // @ts-expect-error this is fine for now
                              value={field.value}
                              onChange={(value) =>
                                changeStops({
                                  originStopAddress: { ...value, id: value?.id ? Number(value?.id) : undefined },
                                })
                              }
                            />
                            {methods.formState.errors.originStop && (
                              <Typography color="error" variant="caption">
                                {methods.formState.errors.originStop.message}
                              </Typography>
                            )}
                          </Guide>
                        )}
                      />
                    </div>
                  )}
                  <div>
                    <Controller
                      control={methods.control}
                      name="originPort.address"
                      render={({ field, fieldState }) => (
                        <Guide
                          showGuide={
                            !field.value && !fieldState.error && routingType !== "D2D" && routingType !== "D2P"
                          }
                        >
                          <LocationPicker
                            label={t("quoteRequest:originAirport")}
                            mode={"AIR"}
                            startIconPallete="grey"
                            required={routingType !== "D2D" && routingType !== "D2P"}
                            highlight={!!methods.formState.errors.originPort}
                            // @ts-expect-error this is fine for now
                            value={field.value}
                            onChange={(value) =>
                              changeStops({
                                originPortAddress: { ...value, id: value?.id ? Number(value?.id) : undefined },
                              })
                            }
                          />
                          {methods.formState.errors.originPort && (
                            <Typography color="error" variant="caption">
                              {methods.formState.errors.originPort.message}
                            </Typography>
                          )}
                        </Guide>
                      )}
                    />
                  </div>
                  <div>
                    <Controller
                      control={methods.control}
                      name="destinationPort.address"
                      render={({ field, fieldState }) => (
                        <Guide
                          showGuide={
                            !field.value && !fieldState.error && routingType !== "P2D" && routingType !== "D2D"
                          }
                        >
                          <LocationPicker
                            label={t("quoteRequest:destinationAirport")}
                            mode={"AIR"}
                            startIconPallete="grey"
                            required={routingType !== "P2D" && routingType !== "D2D"}
                            highlight={!!methods.formState.errors.destinationPort}
                            // @ts-expect-error this is fine for now
                            value={field.value}
                            onChange={(value) =>
                              changeStops({
                                destinationPortAddress: { ...value, id: value?.id ? Number(value?.id) : undefined },
                              })
                            }
                          />
                          {methods.formState.errors.destinationPort && (
                            <Typography color="error" variant="caption">
                              {methods.formState.errors.destinationPort.message}
                            </Typography>
                          )}
                        </Guide>
                      )}
                    />
                  </div>
                  {(routingType === "D2D" || routingType === "P2D") && (
                    <div>
                      <Controller
                        control={methods.control}
                        name="destinationStop.address"
                        render={({ field, fieldState }) => (
                          <Guide showGuide={!field.value && !fieldState.error}>
                            <LocationPicker
                              label={t("quoteRequest:destination")}
                              mode={"AIR"}
                              startIconPallete="grey"
                              toDoor
                              required
                              highlight={!!methods.formState.errors.destinationStop}
                              // @ts-expect-error this is fine for now
                              value={field.value}
                              onChange={(value) =>
                                changeStops({
                                  destinationStopAddress: { ...value, id: value?.id ? Number(value?.id) : undefined },
                                })
                              }
                            />
                            {methods.formState.errors.destinationStop && (
                              <Typography color="error" variant="caption">
                                {methods.formState.errors.destinationStop.message}
                              </Typography>
                            )}
                          </Guide>
                        )}
                      />
                    </div>
                  )}
                  {routingType !== "D2D" && <div />}
                  {(routingType === "P2P" || routingType === undefined) && <div />}

                  <div>
                    <Controller
                      control={methods.control}
                      name="cargo_ready_date"
                      render={({ field, fieldState }) => (
                        <Guide showGuide={!field.value && !fieldState.error}>
                          <SingleCalendarPicker
                            onChange={(value) =>
                              setQuoteRequestAction({ load_spec: { cargo_ready_date: value?.toISO() } })
                            }
                            value={field.value ? DateTime.fromISO(field.value) : null}
                            startIconPallete="grey"
                            label={t("quoteRequest:cargoReadyDate")}
                            required
                            highlight={!!methods.formState.errors.cargo_ready_date}
                          />
                          {methods.formState.errors.destinationStop && (
                            <Typography color="error" variant="caption">
                              {methods.formState.errors.destinationStop.message}
                            </Typography>
                          )}
                        </Guide>
                      )}
                    />
                  </div>
                  <div>
                    <Controller
                      control={methods.control}
                      name="target_delivery_date"
                      render={({ field }) => (
                        <SingleCalendarPicker
                          onChange={(value) => {
                            field.onChange(value);
                            setQuoteRequestAction({ load_spec: { target_delivery_date: value?.toISO() } });
                          }}
                          value={field.value ? DateTime.fromISO(field.value) : null}
                          startIconPallete="grey"
                          label={t("quoteRequest:targetDeliveryDate")}
                          minDate={cargoReadyDate ? DateTime.fromISO(cargoReadyDate) : null}
                          required={!!methods.formState.errors.target_delivery_date}
                        />
                      )}
                    />
                  </div>
                </>
              )}
            </div>
          </Collapse>
        </StepBlock>
      </div>
    </FormProvider>
  );
};

export default RoutingStep;
